import AuthorizedApiClient from 'modules/auth/models/authorizedApiClient';
import { AxiosResponse } from 'axios';
import environment from '@/environment';
import {
  AnalyticsFilters,
  ResponseRate,
  CompletionTime,
  QuestionPerformance,
  UserSegmentation,
  Trend,
  SentimentAnalysis,
  ResponseRatesResponse,
  CompletionTimesResponse,
  QuestionPerformanceResponse,
  UserSegmentationResponse,
  TrendsResponse,
  SentimentAnalysisResponse,
} from '../interfaces/analytics';

export class AnalyticsApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${environment.backendUrl}${environment.apiPrefix}/admin/survey/analytics`;
  }

  /**
   * Get response rates
   */
  public async getResponseRates(filters: AnalyticsFilters = {}): Promise<ResponseRatesResponse> {
    const params: any = {};
    if (filters.questionId) params.questionId = filters.questionId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    const response: AxiosResponse<ResponseRatesResponse> = await AuthorizedApiClient.get(
      `${this.baseUrl}/response-rates`,
      { params },
    );
    return response.data;
  }

  /**
   * Get completion times
   */
  public async getCompletionTimes(
    filters: AnalyticsFilters = {},
  ): Promise<CompletionTimesResponse> {
    const params: any = {};
    if (filters.questionId) params.questionId = filters.questionId;

    const response: AxiosResponse<CompletionTimesResponse> = await AuthorizedApiClient.get(
      `${this.baseUrl}/completion-times`,
      { params },
    );
    return response.data;
  }

  /**
   * Get question performance
   */
  public async getQuestionPerformance(
    filters: AnalyticsFilters = {},
  ): Promise<QuestionPerformanceResponse> {
    // questionIds is required - build params with array format
    const params: any = {};
    
    // Ensure questionIds is always provided (required by backend)
    if (filters.questionIds && filters.questionIds.length > 0) {
      // Send as array: questionIds[]=1&questionIds[]=2
      // Axios handles arrays by default as param[]=value format
      params.questionIds = filters.questionIds;
    } else {
      // Backend requires questionIds parameter, send empty array
      params.questionIds = [];
    }

    const response: AxiosResponse<QuestionPerformanceResponse> = await AuthorizedApiClient.get(
      `${this.baseUrl}/question-performance`,
      { 
        params,
      },
    );
    return response.data;
  }

  /**
   * Get user segmentation
   */
  public async getUserSegmentation(
    filters: AnalyticsFilters = {},
  ): Promise<UserSegmentationResponse> {
    const params: any = {};
    if (filters.userType) params.userType = filters.userType;
    if (filters.milestoneType) params.milestoneType = filters.milestoneType;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    const response: AxiosResponse<UserSegmentationResponse> = await AuthorizedApiClient.get(
      `${this.baseUrl}/user-segmentation`,
      { params },
    );
    return response.data;
  }

  /**
   * Get trends
   */
  public async getTrends(filters: AnalyticsFilters = {}): Promise<TrendsResponse> {
    const params: any = {};
    if (filters.questionId) params.questionId = filters.questionId;
    if (filters.period) params.period = filters.period;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    const response: AxiosResponse<TrendsResponse> = await AuthorizedApiClient.get(
      `${this.baseUrl}/trends`,
      { params },
    );
    return response.data;
  }

  /**
   * Get sentiment analysis
   */
  public async getSentimentAnalysis(
    filters: AnalyticsFilters = {},
  ): Promise<SentimentAnalysisResponse> {
    const params: any = {};
    if (filters.questionId) params.questionId = filters.questionId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;

    const response: AxiosResponse<SentimentAnalysisResponse> = await AuthorizedApiClient.get(
      `${this.baseUrl}/sentiment`,
      { params },
    );
    return response.data;
  }

  /**
   * Export analytics data
   */
  public async exportAnalytics(
    format: 'csv' | 'json',
    filters: AnalyticsFilters = {},
  ): Promise<Blob> {
    const params: any = { format };
    if (filters.questionId) params.questionId = filters.questionId;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.userType) params.userType = filters.userType;
    if (filters.milestoneType) params.milestoneType = filters.milestoneType;

    const response: AxiosResponse<Blob> = await AuthorizedApiClient.get(
      `${this.baseUrl}/export`,
      {
        params,
        responseType: 'blob',
      },
    );
    return response.data;
  }
}
